import React, { useEffect, useState } from 'react';

import { Modal } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import { getCommunityBrochureAndDocs, getFees, getFeesIncentives, getLivingInfo } from 'apis/CommunityAPI';
import { getCommunityShopNotes } from 'apis/ShopNotesAPI';
import { useCommunityInfoContext } from 'components/CommunityInfo/CommunityInfoContext';
import { useRootContext } from 'components/layout/RootContext';
import { ALL_FILTER_OPTION } from 'components/lib/constants';
import GradientDivider from 'components/lib/GradientDivider';
import Loader from 'components/lib/Loader';
import PillSwitcher from 'components/lib/PillSwitcher';
import { FEES_PRICING_TYPES } from 'constants/careTypes';
import { SnapshotDataType } from 'types/snapshot';
import { isNonNegativeNumber } from 'utils/helpers/numbers';

import HistoryDataModalFooter from './HistoryDataModalFooter';
import HistoryDataModalSnapshotContent from './HistoryDataModalSnapshotContent';
import { useSnapshotContext } from '../../../../common/Snapshot/SnapshotContext';
import { FILE_SOURCE, STATUS } from '../../../../constants';
import {
  DOCUMENTS_FIELD,
  FEES_FIELDS_LEVELS,
  FEES_LEVELS_FIELDS,
  FIELD_CATEGORY,
  LEVEL_FEES_FIELDS,
  SHOP_NOTES_FIELD,
} from '../../../ShopData/constants';
import { useCareTypesContext } from '../../careTypes/CareTypesContext';
import { buildDocumentTabs, filterFilesByTab, getFileGroups } from '../../general/documents/helpers';

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-content {
      padding: 0px;
      line-height: 1;
      border-radius: 1px;
      user-select: none;
    }
    .ant-modal-body {
      padding: 0px;
      line-height: normal;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 5px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: auto;
  min-width: 540px;
  max-height: 500px;
`;

const FirstRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0px 30px 2px 30px;
`;

const Data = styled.div`
  display: flex;
  flex-direction: column;
  min-width: fit-content;
  width: 100%;
  &.spaced {
    padding-top: 20px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px;
  padding-bottom: 0px;
`;

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-family: var(--font-regular);
  color: var(--text-secondary);
`;

const Tabs = styled(PillSwitcher)`
  padding: 15px 30px 14px 30px;
`;

const LevelsHeader = styled.div`
  display: flex;
  font-size: 10px;
  color: var(--text-primary);
  justify-content: end;
  margin-bottom: 6px;
`;

const LevelEntry = styled.div`
  display: flex;
  justify-content: center;
  width: 60px;
`;

const HistoryDataModal = () => {
  const { showMessage } = useRootContext();
  const { currentSnapshot, historicalSnapshots } = useSnapshotContext();
  const { careType } = useCareTypesContext();
  const { communityInfo, history, setHistory } = useCommunityInfoContext();

  const [loading, setLoading] = useState(STATUS.IDLE);
  const [data, setData] = useState<any>({});
  const [selectedTab, setSelectedTab] = useState<string>(history?.documentTab ?? ALL_FILTER_OPTION.value);

  useEffect(() => {
    if (!history) return;

    const fetchRecords = async (callback: any, params: any = {}) => {
      setLoading(STATUS.LOADING);

      const values = await Promise.all(
        [currentSnapshot, ...(historicalSnapshots ?? [])].map((snapshot: SnapshotDataType | undefined) =>
          callback(snapshot?.community as number, {
            ...params,
            living_type: careType?.id,
            snapshot: snapshot?.uuid,
          })
            .then((response: any) => [snapshot?.uuid, response])
            .catch(() => {
              showMessage('error', 'Trouble fetching Community Historical data!');
              return [snapshot?.uuid, undefined];
            })
        )
      );
      setLoading(STATUS.LOADED);
      setData(
        Object.fromEntries(
          values.map(([snapshotId, response]) => {
            if ([FIELD_CATEGORY.DOCUMENTS, FIELD_CATEGORY.SHOP_NOTES].includes(history.category)) {
              return [snapshotId, response];
            }

            if (Array.isArray(response)) {
              const match = history.fee_type
                ? response.find((item: any) => item?.fee_type === history.fee_type)
                : response[0];
              return [snapshotId, match ?? {}];
            }

            return [snapshotId, response ?? {}];
          })
        )
      );
    };

    if (history?.category === FIELD_CATEGORY.ACCOMMODATIONS && Boolean(history?.apartment_type)) {
      fetchRecords(getLivingInfo, {
        apartment_type: [history?.apartment_type],
        community_contract_id: history.community_contract_id,
      });
    } else if (history?.category === FIELD_CATEGORY.FEES) {
      fetchRecords(getFees, { community_contract_id: history.community_contract_id });
    } else if (history?.category === FIELD_CATEGORY.FEES_INCENTIVES) {
      fetchRecords(getFeesIncentives, { community_contract_id: history.community_contract_id });
    } else if (history?.category === FIELD_CATEGORY.SHOP_NOTES) {
      fetchRecords(getCommunityShopNotes);
    } else if (history?.category === FIELD_CATEGORY.DOCUMENTS) {
      fetchRecords(getCommunityBrochureAndDocs, { source: [FILE_SOURCE.MEDIA] });
    }
  }, [careType, history]);

  const handleCancel = () => {
    setHistory(undefined);
    setLoading(STATUS.IDLE);
    setData({});
  };

  const subTitle = [DOCUMENTS_FIELD, SHOP_NOTES_FIELD].includes(history?.field as string)
    ? communityInfo?.comunity_name
    : careType?.type;
  const isCareFees =
    history?.category === FIELD_CATEGORY.FEES &&
    [LEVEL_FEES_FIELDS.AVERAGE_COST, FEES_FIELDS_LEVELS].includes(history.field as string);

  // Filter out snapshots that don't have data for the selected field
  // For Care Fees, we need to check if the data has the fees type as we'll allow empty values for history
  const allSnapshots = [currentSnapshot, ...(historicalSnapshots ?? [])] as SnapshotDataType[];

  // Filter out the fields that are not in the data
  const levelsFieldsKeys = Object.values(FEES_LEVELS_FIELDS);

  const allLevelsFields = isCareFees
    ? Array.from(
        new Set(
          allSnapshots
            ?.flatMap((item: SnapshotDataType) =>
              Object.keys(data[item.uuid] || {}).filter((key) => {
                const shouldShowLevelsFields = [
                  FEES_PRICING_TYPES.LEVELS,
                  FEES_PRICING_TYPES.A_LA_CARTE,
                  FEES_PRICING_TYPES.POINTS,
                ].includes(data[item.uuid][LEVEL_FEES_FIELDS.FEES_PRICING_TYPE]);

                return (
                  levelsFieldsKeys.includes(key) && isNonNegativeNumber(data[item.uuid][key]) && shouldShowLevelsFields
                );
              })
            )
            .filter((item: string) => [LEVEL_FEES_FIELDS.AVERAGE_COST, ...levelsFieldsKeys].includes(item))
            .sort()
        )
      )
    : [];

  const hasLevelsFields = Boolean(allLevelsFields.length);

  const isDocuments = history?.category === FIELD_CATEGORY.DOCUMENTS;

  const allDocumentFiles = isDocuments
    ? Object.values(data).flatMap((files: any) => (Array.isArray(files) ? files : []))
    : [];
  const groups = getFileGroups(allDocumentFiles);
  const tabs = buildDocumentTabs(groups);

  const filterDocuments = (files: any) =>
    !isDocuments || !Array.isArray(files) ? files : filterFilesByTab(files, selectedTab);

  return (
    <StyledModal
      open={Boolean(history)}
      width={'fit-content'}
      footer={<HistoryDataModalFooter onCancel={handleCancel} />}
      onCancel={handleCancel}
    >
      <Container>
        <Header>
          <Title>{history?.label} History</Title>
          <Subtitle>{subTitle}</Subtitle>
        </Header>

        {isDocuments && tabs.length > 1 && (
          <Tabs items={tabs} activeKey={selectedTab} onChange={(key) => setSelectedTab(String(key))} />
        )}

        <Content className={'mini-scroll'}>
          <Loader active={loading !== STATUS.LOADED} />
          {loading === STATUS.LOADED && (
            <Data className={classNames({ spaced: !(isCareFees && hasLevelsFields) && !isDocuments })}>
              {allSnapshots?.map((item: SnapshotDataType, index) => {
                const snapshotData = filterDocuments(data[item?.uuid]);
                const hasData = snapshotData && Object.keys(snapshotData).length;
                if (!hasData) {
                  return null;
                }

                const showDivider =
                  !isDocuments &&
                  allSnapshots.length > 1 &&
                  (item.latest || (history?.field === SHOP_NOTES_FIELD && index !== allSnapshots.length - 1)) &&
                  hasData;
                const showTopDivider = isDocuments && hasData;
                return (
                  <React.Fragment key={item.id}>
                    {showTopDivider && <GradientDivider style={{ marginBottom: 15 }} className={'left-anchor flat'} />}
                    {item.latest && hasLevelsFields && isCareFees && (
                      <FirstRow>
                        <LevelsHeader>
                          <LevelEntry>Average</LevelEntry>
                          {allLevelsFields?.map((item: string) => (
                            <LevelEntry key={item}>Lv. {item.split('_')[1]}</LevelEntry>
                          ))}
                        </LevelsHeader>
                      </FirstRow>
                    )}
                    <HistoryDataModalSnapshotContent
                      key={item.id}
                      levelFields={allLevelsFields}
                      snapshot={item}
                      data={snapshotData}
                    />
                    {showDivider && <GradientDivider style={{ marginBottom: 15 }} className={'left-anchor flat'} />}
                  </React.Fragment>
                );
              })}
            </Data>
          )}
        </Content>
      </Container>
    </StyledModal>
  );
};

export default HistoryDataModal;
